import React, { useEffect, useState } from "react";
import { AuthenticationAdminResponse, BatchDTO, ClassListItem, CourseListItem, CustomerDTO, EntityListEnum, GetMetricsResponse, ProductDTO, ProvisionedBatchDTO, Roles, SchoolListItem, SiteDTO, UserListItem } from "@beable/roster-backend-client-v1";
import {
  Box, CircularProgress, Container, Grid, Link, Typography
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import authService from "../../services/AuthService";
import metricsService from "../../services/MetricsService";
import CustomerAndSiteSelector from "../Common/CustomerAndSiteSelector";
import BasePanel from "../Common/Layout/BasePanel";
import { BasePanelBox } from "../Common/Layout/BasePanelBox";
import BaseSheet from "../Common/Layout/BaseSheet";
import { StyledPanelHeader } from "../Common/Layout/PanelHeader";
import Header from "../Header/Header";
import MetricsContainer from "./MetricsContainer/MetricsContainer";
import MetricsModal from "./MetricsContainer/MetricsModal";
import { getStyles } from './styles';

const dateOptions: Intl.DateTimeFormatOptions = {
  hour12: true,
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
};

const initialSelectedSite: SiteDTO | null = null;
const initialLastRosteredBatch: BatchDTO | null = null;
const initialLastProvisionedBatches: ProvisionedBatchDTO[] = [];
const initialMetricsValue: GetMetricsResponse | null = null;
const initialModalDataRowNumbers: Array<number> = [0, 99];
const initialModalSite: SiteDTO | undefined = undefined;
const initialModalTitle: EntityListEnum | undefined = undefined;

export default function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = getStyles(theme);

  const [isMetricsLoading, setIsMetricsLoading] = useState(false);
  const [isMetricsModalOpened, setIsMetricsModalOpened] = useState(false);
  const [isModalDataLoading, setIsModalDataLoading] = useState(false);
  const [modalData, setModalData] = useState<SchoolListItem[] | CourseListItem[] | ClassListItem[] | UserListItem[] | ProductDTO[]>([]);
  const [modalTitle, setModalTitle] = useState(initialModalTitle);
  const [modalDataRowNumbers, setModalDataRowNumbers] = useState(
    initialModalDataRowNumbers
  );
  const [modalSite, setModalSite] = useState(initialModalSite);
  const [selectedSite, setSelectedSite] = useState(initialSelectedSite);
  const [lastRosteredBatch, setLastRosteredBatch] = useState(
    initialLastRosteredBatch
  );
  const [lastProvisionedBatches, setLastProvisionedBatches] = useState(
    initialLastProvisionedBatches
  );
  const [metrics, setMetrics] = useState(initialMetricsValue);
  const [admin, setAdmin] = useState<AuthenticationAdminResponse | undefined>();
  const [customer, setCustomer] = useState<CustomerDTO>();

  const openMetricsModal = (title: EntityListEnum) => {
    if (metrics && EntityListEnum[title]) {
      setModalTitle(title);
      setModalSite(metrics.site);
      setIsMetricsModalOpened(true);
      getModalData(title, metrics.site.id, 0, 99);
    }
  };

  const getModalData = async (
    title: EntityListEnum,
    siteId: number,
    fromRow: number,
    toRow: number
  ) => {
    if (title && siteId) {
      setIsModalDataLoading(true);
      const data = await metricsService.getEntityData(
        siteId,
        title,
        fromRow,
        toRow
      );
      if (data) {
        setModalData(data);
        setIsModalDataLoading(false);
      } else {
        setModalData([]);
        setIsModalDataLoading(false);
      }
    }
  };

  const closeMetricsModal = () => {
    setIsMetricsModalOpened(false);
    setModalSite(initialModalSite);
    setModalTitle(initialModalTitle);
    setModalDataRowNumbers(initialModalDataRowNumbers);
    setModalData([]);
  };

  const resetSiteLevelStates = () => {
    setMetrics(initialMetricsValue);
    setLastRosteredBatch(initialLastRosteredBatch);
    setLastProvisionedBatches(initialLastProvisionedBatches);
  }

  const resetCustomerLevelStates = () => {
    setSelectedSite(initialSelectedSite);
    resetSiteLevelStates();
  }

  const handleCustomerChange = (customer: CustomerDTO) => {
    if (customer) {
      resetCustomerLevelStates();
      setCustomer(customer);
    }
  };

  const fetchRosteredMetrics = async (customerId: string) => {
    try {
      setIsMetricsLoading(true);
      const metrics = await metricsService.getMetrics(selectedSite!.id, customerId);
      
      if (metrics) {
        setLastRosteredBatch(metrics.lastRosteredBatch);
        setLastProvisionedBatches(metrics.lastProvisionedBatches);
        setMetrics(metrics);
      } else {
        window.alert("Could not load the dashboard");
      }
      setIsMetricsLoading(false);
    } catch (error) {
      console.error(error)
      setIsMetricsLoading(false);
    }
  };

  useEffect(() => {
    if (
      admin &&
      ([Roles.itadmin, Roles.districtadmin, Roles.beableadmin].includes(admin.role)) &&
      customer &&
      selectedSite
    ) {
      fetchRosteredMetrics(customer.id);
    }
  }, [selectedSite]);

  useEffect(() => {
    const adminResp = authService.getUser();
    setAdmin(adminResp);
  }, []);
  
  const handleSiteChange = (site: SiteDTO) => {
    if (site) setSelectedSite(site);
    else resetSiteLevelStates();
  };

  const setPreviousPage = () => {
    if (modalDataRowNumbers[0] > 99 && modalDataRowNumbers[1] > 198) {
      const newStartRow = modalDataRowNumbers[0] - 100;
      const newEndRow = modalDataRowNumbers[1] - 100;
      setModalDataRowNumbers([newStartRow, newEndRow]);
      if (modalTitle && modalSite) {
        getModalData(modalTitle, modalSite.id, newStartRow, newEndRow);
      }
    }
  };

  const setNextPage = () => {
    const newStartRow = modalDataRowNumbers[0] + 100;
    const newEndRow = modalDataRowNumbers[1] + 100;

    setModalDataRowNumbers([newStartRow, newEndRow]);
    if (modalTitle && modalSite)
      getModalData(modalTitle, modalSite.id, newStartRow, newEndRow);
  };

  const provisionFileLocation = (fileLocation: string | null) => {
    if (!fileLocation) {
      return "No file available";
    }

    if (fileLocation.includes("http")) {
      return (
        <a href={fileLocation} download>
          Download Export File
        </a>
      );
    }
    return <Typography component="span" sx={styles.errorSpan}>{fileLocation}</Typography>;
  };

  const goToBatchStatus = () => {
    navigate(`/batch-status`);
  };

  const goToProvision = () => {
    navigate(`/provision-users`);
  };

  const getContainers = () => {
    return metrics && [
      {
        dataKey: EntityListEnum.products,
        title: "Products",
        metrics: { available: metrics.products.length }
      },
      {
        dataKey: EntityListEnum.schools,
        title: "Schools",
        metrics: { rostered: metrics.schools.rostered, active: metrics.schools.active, provisioned: metrics.schools.provisioned }
      },
      {
        dataKey: EntityListEnum.courses,
        title: "Courses",
        metrics: { rostered: metrics.courses.rostered, active: metrics.courses.active, provisioned: metrics.courses.provisioned }
      },
      {
        dataKey: EntityListEnum.classes,
        title: "Classes",
        metrics:  { rostered: metrics.classes.rostered, active: metrics.classes.active, provisioned: metrics.classes.provisioned }
      },
      {
        dataKey: EntityListEnum.administrators,
        title: "Administrators",
        metrics: { rostered: metrics.administrators.rostered, active: metrics.administrators.active, provisioned: metrics.administrators.provisioned }
      },
      {
        dataKey: EntityListEnum.teachers,
        title: "Teachers",
        metrics: { rostered: metrics.teachers.rostered, active: metrics.teachers.active, provisioned: metrics.teachers.provisioned }
      },
      {
        dataKey: EntityListEnum.students,
        title: "Students",
        metrics: { rostered: metrics.students.rostered, active: metrics.students.active, provisioned: metrics.students.provisioned }
      }
    ];
  }

  return (
    <>
      <Header />
      <Grid container>
        <BasePanel>
          <BasePanelBox>
            <StyledPanelHeader header="Monitor Account Activity" />
            <Grid container size={12}>
              <CustomerAndSiteSelector
                onCustomerSelect={handleCustomerChange}
                onSiteSelect={handleSiteChange}
                displayCustomerCard={false}
              />
            </Grid>
          </BasePanelBox>
        </BasePanel>
        <BaseSheet maxWidth="lg">
          <Grid size={12}>
            {isMetricsLoading ? (
              <Box sx={styles.loaderContainer}>
                <Box sx={styles.placeHolder}>
                  <CircularProgress disableShrink />
                </Box>
              </Box>
            ) : null}

            {!isMetricsLoading && selectedSite && metrics &&
              <div key={selectedSite.id}>
                <Typography variant="h5">
                  Site: {selectedSite.subdomain}
                </Typography>
                <Container sx={styles.metricsContainer}>
                  { getContainers()?.map((containerMetrics, metricIndex) => (
                      <MetricsContainer
                        title={containerMetrics.title}
                        metrics={containerMetrics.metrics}
                        isLoading={isMetricsLoading}
                        key={`${containerMetrics.dataKey}-${metricIndex}`}
                        bgcolor={metricIndex%2 ? '#1f93c6' : '#19739d' }
                        dataKey={containerMetrics.dataKey}
                        onClick={openMetricsModal} 
                      />
                  ))}
                </Container>
                <Container sx={{ marginTop: "16px" }}>
                  <Typography variant="h6">Roster Updates:</Typography>
                  {lastRosteredBatch?.id ? (
                    <Typography variant="body2">
                      {`Ran on
                      ${new Date(
                        lastRosteredBatch.createdAt
                      ).toLocaleDateString("en-US", dateOptions)}
                      , Filename: ${
                        lastRosteredBatch.uploadedFileName
                      }
                      , Processing Status:  ${
                        lastRosteredBatch.status
                      }`}
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      No batches uploaded
                    </Typography>
                  )}
                  <Link
                    onClick={() => {
                      goToBatchStatus();
                    }}
                    color="primary"
                    component="button"
                    variant="body2"
                    underline="hover">
                    Batch Status
                  </Link>
                  <br />
                  <br />
                  <Typography variant="h6">Provision Status:</Typography>
                  {lastProvisionedBatches?.length ? (
                    <Typography variant="body2">
                      Latest provisioning run:{" "}
                    </Typography>
                  ) : null}
                  {lastProvisionedBatches?.length ? (
                    lastProvisionedBatches.map(
                      (lastProvisionedBatch: ProvisionedBatchDTO) => {
                        return (
                          <Typography
                            variant="body2"
                            key={lastProvisionedBatch.id}
                            id="provisioned-runs"
                          >
                            Finished at{" "}
                            {lastProvisionedBatch.finishedAt
                              ? new Date(
                                  lastProvisionedBatch.finishedAt
                                ).toLocaleDateString("en-US", dateOptions)
                              : "In progress"}
                            {lastProvisionedBatch.sessionName
                              ? ` Session: ${lastProvisionedBatch.sessionName}`
                              : null}
                            {lastProvisionedBatch.productName
                              ? ` Product: ${lastProvisionedBatch.productName}`
                              : null}
                            {provisionFileLocation(
                              lastProvisionedBatch.fileLocation
                            )}
                          </Typography>
                        );
                      }
                    )
                  ) : (
                    <Typography variant="body2">
                      No users provisioned
                    </Typography>
                  )}
                  <Link
                    onClick={() => {
                      goToProvision();
                    }}
                    color="primary"
                    component="button"
                    variant="body2"
                    underline="hover">
                    Provision Users
                  </Link>
                </Container>
              </div>
            }

            <MetricsModal
              open={isMetricsModalOpened}
              handleClose={closeMetricsModal}
              dataRows={modalData}
              title={modalTitle}
              isLoading={isModalDataLoading}
              rowNumbers={modalDataRowNumbers}
              setPreviousPage={setPreviousPage}
              setNextPage={setNextPage}
            />
          </Grid>
        </BaseSheet>
      </Grid>
    </>
  );
}
